// src/pages/AnalyticsPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Field, Select, Button, Input } from '../ui';
import { useCountry } from '../context/CountryContext';
import ReservationDrawer from '../components/ReservationDrawer';
import AlertOverlay from '../components/AlertOverlay';
import { getReservationCarSegments } from '../utils/reservationCarSegments';
import { ExternalLink } from 'lucide-react';

const client = createAuditedClient('AnalyticsPage');

/* ================= GraphQL ================= */

const ListCompanies = /* GraphQL */ `
  query ListCompanies($limit: Int, $nextToken: String) {
    listCompanies(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        country
        currencies
        pickupLocations
        dropoffLocations
      }
      nextToken
    }
  }
`;

const ListCars = /* GraphQL */ `
  query ListCars($filter: ModelCarFilterInput, $limit: Int, $nextToken: String) {
    listCars(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        name
        plate
        active
        fuelType
        transmission
        year
        category
        bodyType
        color
        imageKey
        startingPrice
        seats
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const ListReservations = /* GraphQL */ `
  query ListReservations($filter: ModelReservationFilterInput, $limit: Int, $nextToken: String) {
    listReservations(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        carId
        originalCarId
        originalCarName
        originalCarPlate
        carSubstitutions
        status
        title
        start
        end
        pickupAt
        pickupLocation
        dropoffAt
        dropoffLocation
        reservationDate
        reservationRef
        customerName
        customerEmail
        customerPhone
        flightNumber
        phoneNumbers
        babyCarSeat
        extras
        rentalAmount
        rentalCurrency
        depositAmount
        depositCurrency
        platform
        senderEmail
        acceptUrl
        declineUrl
        notes
        approvalStatus
        pickupChecklistStatus
        dropoffChecklistStatus
        paymentStatus
        amountPaid
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const ListAccountingEntries = /* GraphQL */ `
  query ListAccountingEntries($filter: ModelAccountingEntryFilterInput, $limit: Int, $nextToken: String) {
    listAccountingEntries(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        date
        type
        category
        description
        amount
        currency
        carId
        reservationId
        notes
        createdAt
      }
      nextToken
    }
  }
`;

const GetCompany = /* GraphQL */ `
  query GetCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
      country
      currencies
      pickupLocations
      dropoffLocations
    }
  }
`;

/* ================= Helpers ================= */

function parseDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toISODate(dt) {
  if (!dt) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fmtMonthRangeLabel(monthRange, monthFilter) {
  if (!monthRange) return 'All time';
  // monthFilter is "YYYY-MM"
  if (monthFilter) return monthFilter;
  const s = monthRange.start.toLocaleDateString();
  const e = new Date(monthRange.end.getTime() - 1).toLocaleDateString();
  return `${s} → ${e}`;
}
function fmtInt(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(x));
}

function fmtMoney0(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(x);
}

// Currency map formatter with commas + NO decimals
function formatCurrencyMap0(map) {
  const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  if (!entries.length) return '—';
  return entries.map(([cur, val]) => `${cur} ${fmtMoney0(val)}`).join(' • ');
}

function getMonthRange(monthStr) {
  if (!monthStr) return null;
  const [yStr, mStr] = monthStr.split('-');
  const year = Number(yStr);
  const monthIndex = Number(mStr) - 1;
  if (!year || monthIndex < 0 || monthIndex > 11) return null;

  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 1);
  return { start, end };
}

function getCurrentMonthValue() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`; // "YYYY-MM"
}


function getLifecycleStatus(r, now = new Date()) {
  const approval = String(r?.approvalStatus || 'PENDING').toUpperCase();
  const start = parseDate(r?.start);
  const end = parseDate(r?.end);

  // Not approved yet -> still pending approval
  if (approval !== 'APPROVED') return 'PENDING_APPROVAL';

  // Approved but missing/invalid dates -> data problem
  if (!start || !end) return 'APPROVED_NO_DATES';

  if (end <= now) return 'COMPLETED';
  if (start <= now && now < end) return 'ACTIVE';
  if (now < start) return 'UPCOMING';

  return 'UNKNOWN';
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

async function listAllPages({ query, variables, authMode, pageLimit = 1000, maxPages = 25 }) {
  let nextToken = null;
  const out = [];
  for (let i = 0; i < maxPages; i++) {
    const res = await client.graphql({
      query,
      variables: { ...(variables || {}), limit: pageLimit, nextToken },
      authMode,
    });

    // Detect which list we got by reading the first field of data
    const data = res?.data || {};
    const rootKey = Object.keys(data)[0];
    const payload = data[rootKey];

    const items = (payload?.items || []).filter(Boolean);
    out.push(...items);

    nextToken = payload?.nextToken || null;
    if (!nextToken) break;
  }
  return out;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);

    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);

    setMatches(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function carDisplayName(car) {
  if (!car) return 'Unknown vehicle';
  const name = car.name || car.id || 'Unknown vehicle';
  return car.plate ? `${name} (${car.plate})` : name;
}

function formatDateTimeShort(value) {
  const dt = parseDate(value);
  if (!dt) return '--';
  return dt.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(value) {
  const dt = parseDate(value);
  if (!dt) return '--';
  return dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function sortReservationsDesc(a, b) {
  const ad = parseDate(a?.start) || parseDate(a?.createdAt) || new Date(0);
  const bd = parseDate(b?.start) || parseDate(b?.createdAt) || new Date(0);
  return bd.getTime() - ad.getTime();
}

function rangeToIso(range) {
  if (!range?.start || !range?.end) return null;
  return {
    start: range.start.toISOString(),
    end: range.end.toISOString(),
  };
}

function segmentOverlapsRange(segment, range) {
  if (!segment?.start || !segment?.end) return false;
  const isoRange = rangeToIso(range);
  if (!isoRange) return true;
  return segment.start < isoRange.end && isoRange.start < segment.end;
}

function getVehicleSegmentsForReservation(reservation, vehicleId) {
  const target = String(vehicleId || '');
  if (!target || !reservation) return [];

  const segments = getReservationCarSegments(reservation).filter(
    segment => String(segment.carId || '') === target
  );
  if (segments.length) return segments;

  const directMatch = [reservation.carId, reservation.originalCarId].some(
    id => String(id || '') === target
  );
  const start = parseDate(reservation.start || reservation.pickupAt);
  const end = parseDate(reservation.end || reservation.dropoffAt);
  if (!directMatch || !start || !end || end <= start) return [];

  return [{
    carId: target,
    start: start.toISOString(),
    end: end.toISOString(),
    isSubstitution: String(reservation.originalCarId || '') === target && String(reservation.carId || '') !== target,
  }];
}

function reservationUsesVehicleInRange(reservation, vehicleId, range = null) {
  return getVehicleSegmentsForReservation(reservation, vehicleId).some(
    segment => segmentOverlapsRange(segment, range)
  );
}

function formatVehicleReservationWindow(reservation, vehicleId, range = null) {
  const segments = getVehicleSegmentsForReservation(reservation, vehicleId)
    .filter(segment => segmentOverlapsRange(segment, range));

  if (!segments.length) return '--';
  if (segments.length === 1) {
    return `${formatDateShort(segments[0].start)} - ${formatDateShort(segments[0].end)}`;
  }

  return `${segments.length} vehicle segments`;
}

function reservedDaysForVehicle(reservations, vehicleId, range = null) {
  const isoRange = rangeToIso(range);
  let ms = 0;

  for (const reservation of reservations) {
    for (const segment of getVehicleSegmentsForReservation(reservation, vehicleId)) {
      if (!segmentOverlapsRange(segment, range)) continue;

      const startIso = isoRange && segment.start < isoRange.start ? isoRange.start : segment.start;
      const endIso = isoRange && segment.end > isoRange.end ? isoRange.end : segment.end;
      const start = parseDate(startIso);
      const end = parseDate(endIso);
      if (start && end && end > start) ms += end - start;
    }
  }

  return ms / (24 * 60 * 60 * 1000);
}

function currencyTotalsFromReservations(reservations, amountField, currencyField) {
  const map = new Map();
  for (const reservation of reservations) {
    if (reservation?.[amountField] == null || !reservation?.[currencyField]) continue;
    const cur = String(reservation[currencyField]).toUpperCase();
    map.set(cur, (map.get(cur) || 0) + safeNumber(reservation[amountField]));
  }
  return map;
}

/* ================= Small chart components (no deps) ================= */

function MiniBarChart({ title, subtitle, rows, height = 140 }) {
  // rows: [{ label, value }]
  const max = useMemo(() => {
    const m = Math.max(0, ...rows.map(r => safeNumber(r.value)));
    return m || 1;
  }, [rows]);

  return (
    <div style={miniCard()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{subtitle}</div> : null}
        </div>
      </div>

      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
        {rows.length === 0 ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>No data.</div>
        ) : (
          rows.map((r, idx) => {
            const v = safeNumber(r.value);
            const pct = clamp((v / max) * 100, 0, 100);
            return (
              <div key={`${r.label}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 62px', gap: 10, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div title={r.label} style={{ fontSize: 12, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.label}
                    </div>
                  </div>
                  <div style={{ height: 10, background: '#eef2ff', borderRadius: 999, overflow: 'hidden', marginTop: 6 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#4f46e5' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 800, color: '#111827' }}>
                  {fmtInt(v)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* spacer to keep consistent card height */}
      <div style={{ height: Math.max(0, height - 24 - rows.length * 32) }} />
    </div>
  );
}

function Sparkline({ points, height = 42 }) {
  // points: number[]
  const w = 220;
  const h = height;
  const pad = 6;
  const max = Math.max(0, ...points.map(safeNumber));
  const min = Math.min(0, ...points.map(safeNumber));
  const span = max - min || 1;

  const d = points
    .map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / Math.max(1, points.length - 1);
      const y = pad + ((max - safeNumber(v)) * (h - pad * 2)) / span;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <path d={d} fill="none" stroke="#0f172a" strokeWidth="2" opacity="0.18" />
      <path d={d} fill="none" stroke="#0ea5e9" strokeWidth="2.5" />
    </svg>
  );
}

function StatCard({ label, value, sub, right }) {
  return (
    <div style={kpiCard()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
          <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900, color: '#111827', lineHeight: 1.1 }}>
            {value}
          </div>
          {sub ? <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>{sub}</div> : null}
        </div>
        {right ? <div style={{ width: 120, opacity: 0.95 }}>{right}</div> : null}
      </div>
    </div>
  );
}

/* ================= Main ================= */

export default function AnalyticsPage() {
  const [ready, setReady] = useState(false);
  const [claims, setClaims] = useState(null);
  const [groups, setGroups] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [cars, setCars] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const vehicleLookupRef = useRef(null);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitial, setDrawerInitial] = useState(null);
  const [alert, setAlert] = useState(null);

  const [monthFilter, setMonthFilter] = useState(getCurrentMonthValue);
  const monthRange = useMemo(() => getMonthRange(monthFilter), [monthFilter]);

  const isAdmin = useMemo(() => groups.includes('admin'), [groups]);
  const canViewAnalytics = isAdmin;

  const myCompanyId = claims?.CompanyId || null;

  const selectedCompany = useMemo(
    () => companies.find(c => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const { selectedCompanyId: ctxCompanyId } = useCountry();

  /* ===== Toast ===== */
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  function showAlert(message, variant = 'info') {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3500);
  }

  function hideAlert() {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = null;
    setToast(null);
  }

  function pushAlert(payload) {
    setAlert({
      id: Date.now(),
      type: payload?.type || 'info',
      title: payload?.title,
      message: payload?.message,
      detail: payload?.detail,
      confirmLabel: payload?.confirmLabel,
      cancelLabel: payload?.cancelLabel,
      onConfirm: payload?.onConfirm,
      onCancel: payload?.onCancel,
    });
  }

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  /* -------- auth -------- */
  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        const g = p['cognito:groups'] || [];
        setGroups(Array.isArray(g) ? g : []);
        setClaims({
          CompanyId: p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
          sub: p.sub || null,
          email: p.email || null,
          groups: Array.isArray(g) ? g : [],
        });
      } catch (e) {
        console.error('AnalyticsPage auth failed', e);
        setGroups([]);
        setClaims(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  /* -------- companies -------- */
  useEffect(() => {
    if (!ready) return;

    (async () => {
      try {
        if (isAdmin) {
          const items = await listAllPages({
            query: ListCompanies,
            variables: {},
            authMode: 'userPool',
            pageLimit: 1000,
          });
          const sorted = items
            .filter(Boolean)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setCompanies(sorted);
        } else {
          if (!myCompanyId) {
            setCompanies([]);
            setSelectedCompanyId(null);
            return;
          }

          const res = await client.graphql({
            query: GetCompany,
            variables: { id: myCompanyId },
            authMode: 'userPool',
          });

          const co = res.data?.getCompany;
          if (co) {
            setCompanies([co]);
            setSelectedCompanyId(co.id);
          } else {
            setCompanies([{ id: myCompanyId, name: myCompanyId }]);
            setSelectedCompanyId(myCompanyId);
          }
        }
      } catch (e) {
        console.error('Load companies failed (analytics)', e);
        if (isAdmin) {
          setCompanies([]);
          showAlert('Failed to load agencies. Check console/logs.', 'error');
        } else {
          if (myCompanyId) {
            setCompanies([{ id: myCompanyId, name: myCompanyId }]);
            setSelectedCompanyId(myCompanyId);
          } else {
            setCompanies([]);
            setSelectedCompanyId(null);
          }
          showAlert('Failed to load your agency. Check console/logs.', 'error');
        }
      }
    })();
  }, [ready, isAdmin, myCompanyId]);

  /* -------- sync selection with country context -------- */
  useEffect(() => {
    if (!ready || !isAdmin || !companies.length) return;

    if (ctxCompanyId && companies.some(c => c.id === ctxCompanyId)) {
      if (selectedCompanyId !== ctxCompanyId) setSelectedCompanyId(ctxCompanyId);
      return;
    }

    if (!selectedCompanyId) setSelectedCompanyId(companies[0].id);
  }, [ready, isAdmin, companies, ctxCompanyId, selectedCompanyId]);

  /* -------- data loader (fixed refresh) -------- */
  const [reloadNonce, setReloadNonce] = useState(0);
  const refresh = useCallback(() => setReloadNonce(n => n + 1), []);

  const loadData = useCallback(async () => {
    if (!ready || !canViewAnalytics || !selectedCompanyId) return;
    setLoading(true);
    try {
      const [carItems, resItems, accItems] = await Promise.all([
        listAllPages({
          query: ListCars,
          variables: { filter: { CompanyId: { eq: selectedCompanyId } } },
          authMode: 'userPool',
          pageLimit: 1000,
        }),
        listAllPages({
          query: ListReservations,
          variables: { filter: { CompanyId: { eq: selectedCompanyId } } },
          authMode: 'userPool',
          pageLimit: 1000,
        }),
        listAllPages({
          query: ListAccountingEntries,
          variables: { filter: { CompanyId: { eq: selectedCompanyId } } },
          authMode: 'userPool',
          pageLimit: 1000,
        }),
      ]);

      setCars(carItems);
      setReservations(resItems);
      setAccountingEntries(
        accItems
          .filter(Boolean)
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      );
    } catch (e) {
      console.error('Load analytics data failed', e);
      setCars([]);
      setReservations([]);
      setAccountingEntries([]);
      showAlert('Failed to load analytics data. Check console/logs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [ready, canViewAnalytics, selectedCompanyId]);

  useEffect(() => {
    loadData();
  }, [loadData, reloadNonce]);

  useEffect(() => {
    setSelectedVehicleId('');
    setVehicleSearch('');
    setDrawerOpen(false);
    setDrawerInitial(null);
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedVehicleId) return;
    if (!cars.some(car => car.id === selectedVehicleId)) setSelectedVehicleId('');
  }, [cars, selectedVehicleId]);

  /* -------- filtered reservations by month -------- */
  const filteredReservations = useMemo(() => {
    if (!monthRange) return reservations;
    const { start, end } = monthRange;
    return reservations.filter(r => {
      const dt = parseDate(r.start) || parseDate(r.createdAt);
      if (!dt) return false;
      return dt >= start && dt < end;
    });
  }, [reservations, monthRange]);

  const totalReservationsCount = reservations.length;
  const filteredCount = filteredReservations.length;

  /* -------- reservation stats -------- */
  const reservationStats = useMemo(() => {
    const out = {
      total: 0,
      byStatus: {},
      byApproval: {},
    };

    const now = new Date();

    for (const r of filteredReservations) {
      out.total += 1;

      const lifecycle = getLifecycleStatus(r, now);
      out.byStatus[lifecycle] = (out.byStatus[lifecycle] || 0) + 1;

      const ap = r.approvalStatus || 'Unknown';
      out.byApproval[ap] = (out.byApproval[ap] || 0) + 1;
    }

    return out;
  }, [filteredReservations]);

  const topStatusRows = useMemo(() => {
    return Object.entries(reservationStats.byStatus)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [reservationStats.byStatus]);

  const topApprovalRows = useMemo(() => {
    return Object.entries(reservationStats.byApproval)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [reservationStats.byApproval]);

  /* -------- platform stats (NO mixed currency totals) -------- */
  const platformStats = useMemo(() => {
    const map = new Map(); // platform -> { count, rentalMap, depositMap }
    for (const r of filteredReservations) {
      const key = r.platform || 'Unknown';
      if (!map.has(key)) {
        map.set(key, { count: 0, rental: new Map(), deposit: new Map() });
      }
      const bucket = map.get(key);
      bucket.count += 1;

      if (r.rentalAmount != null && r.rentalCurrency) {
        const cur = String(r.rentalCurrency).toUpperCase();
        bucket.rental.set(cur, (bucket.rental.get(cur) || 0) + safeNumber(r.rentalAmount));
      }
      if (r.depositAmount != null && r.depositCurrency) {
        const cur = String(r.depositCurrency).toUpperCase();
        bucket.deposit.set(cur, (bucket.deposit.get(cur) || 0) + safeNumber(r.depositAmount));
      }
    }

    return Array.from(map.entries())
      .map(([platform, v]) => ({
        platform,
        reservations: v.count,
        rentalByCurrency: v.rental,
        depositByCurrency: v.deposit,
      }))
      .sort((a, b) => b.reservations - a.reservations);
  }, [filteredReservations]);

  const platformChartRows = useMemo(() => {
    return platformStats
      .slice()
      .sort((a, b) => b.reservations - a.reservations)
      .slice(0, 8)
      .map(p => ({ label: p.platform, value: p.reservations }));
  }, [platformStats]);

  /* -------- reservations trend (count per day) -------- */
  const trend = useMemo(() => {
    // if no month selected, show last 30 days based on start/createdAt
    let start = monthRange?.start || null;
    let end = monthRange?.end || null;

    const allDts = filteredReservations
      .map(r => parseDate(r.start) || parseDate(r.createdAt))
      .filter(Boolean);

    if (!start || !end) {
      const maxDt = allDts.length ? new Date(Math.max(...allDts.map(d => d.getTime()))) : new Date();
      end = new Date(maxDt.getFullYear(), maxDt.getMonth(), maxDt.getDate() + 1);
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    const days = [];
    const dayMap = new Map(); // yyyy-mm-dd -> count
    for (const r of filteredReservations) {
      const dt = parseDate(r.start) || parseDate(r.createdAt);
      if (!dt) continue;
      if (dt < start || dt >= end) continue;
      const key = toISODate(dt);
      dayMap.set(key, (dayMap.get(key) || 0) + 1);
    }

    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (cursor < end) {
      const key = toISODate(cursor);
      days.push({ key, count: dayMap.get(key) || 0 });
      cursor.setDate(cursor.getDate() + 1);
    }

    return days;
  }, [filteredReservations, monthRange]);

  const sparkPoints = useMemo(() => trend.map(d => d.count), [trend]);
  const trendTotal = useMemo(() => sparkPoints.reduce((a, b) => a + safeNumber(b), 0), [sparkPoints]);
  const trendPeak = useMemo(() => Math.max(0, ...sparkPoints.map(safeNumber)), [sparkPoints]);

  /* -------- car stats + utilization -------- */
  const carStats = useMemo(() => {
    const msPerDay = 24 * 60 * 60 * 1000;
    const periodStart = monthRange?.start || null;
    const periodEnd = monthRange?.end || null;

    const carMapStats = new Map();
    for (const c of cars) {
      carMapStats.set(c.id, {
        carId: c.id,
        name: c.name,
        plate: c.plate,
        active: !!c.active,
        reservationCount: 0,
        totalRental: new Map(), // currency -> sum
        lastReservation: null,
        reservedMs: 0,
        reservedDays: 0,
        utilizationPct: null,
      });
    }

    for (const r of filteredReservations) {
      const bucket = carMapStats.get(r.carId);
      if (!bucket) continue;

      bucket.reservationCount += 1;

      if (r.rentalAmount != null && r.rentalCurrency) {
        const cur = String(r.rentalCurrency).toUpperCase();
        bucket.totalRental.set(cur, (bucket.totalRental.get(cur) || 0) + safeNumber(r.rentalAmount));
      }

      const resStart = parseDate(r.start);
      const resEnd = parseDate(r.end);
      if (resStart && resEnd && resEnd > resStart) {
        let overlapStart = resStart;
        let overlapEnd = resEnd;

        if (periodStart && periodEnd) {
          overlapStart = resStart > periodStart ? resStart : periodStart;
          overlapEnd = resEnd < periodEnd ? resEnd : periodEnd;
        }

        if (overlapEnd > overlapStart) {
          bucket.reservedMs += overlapEnd - overlapStart;
        }
      }

      const dt = parseDate(r.start) || parseDate(r.createdAt);
      if (dt) {
        if (!bucket.lastReservation || dt > bucket.lastReservation) bucket.lastReservation = dt;
      }
    }

    const periodDurationMs =
      monthRange && monthRange.end > monthRange.start ? monthRange.end - monthRange.start : null;

    for (const bucket of carMapStats.values()) {
      bucket.reservedDays = bucket.reservedMs > 0 ? bucket.reservedMs / msPerDay : 0;
      bucket.utilizationPct =
        periodDurationMs && periodDurationMs > 0
          ? Math.min(100, (bucket.reservedMs / periodDurationMs) * 100)
          : null;
    }

    return Array.from(carMapStats.values()).sort((a, b) => b.reservationCount - a.reservationCount);
  }, [cars, filteredReservations, monthRange]);

  const fleetUtilization = useMemo(() => {
    if (!monthRange || !cars.length || !carStats.length) return null;
    const periodDurationMs = monthRange.end - monthRange.start;
    if (periodDurationMs <= 0) return null;

    const usedMs = carStats.reduce((sum, c) => sum + c.reservedMs, 0);
    const totalCapacityMs = periodDurationMs * cars.length;
    if (!totalCapacityMs) return null;

    const avgReservedDaysPerCar =
      carStats.reduce((sum, c) => sum + c.reservedDays, 0) / cars.length;

    return {
      utilizationPct: (usedMs / totalCapacityMs) * 100,
      avgReservedDaysPerCar,
    };
  }, [monthRange, cars.length, carStats]);

  const topUtilization = useMemo(() => {
    if (!monthRange) return [];
    return carStats
      .filter(c => c.utilizationPct != null)
      .slice()
      .sort((a, b) => (b.utilizationPct || 0) - (a.utilizationPct || 0))
      .slice(0, 8)
      .map(c => ({ label: c.plate || c.name || c.carId, value: c.utilizationPct || 0 }));
  }, [carStats, monthRange]);

  /* -------- ledger stats (accounting) -------- */
  const accountingEntriesInRange = useMemo(() => {
    if (!accountingEntries.length) return [];
    if (!monthRange) return accountingEntries;

    const { start, end } = monthRange;
    return accountingEntries.filter(e => {
      const dt = parseDate(e.date);
      if (!dt) return false;
      return dt >= start && dt < end;
    });
  }, [accountingEntries, monthRange]);

  const ledgerTotalsByCurrency = useMemo(() => {
    const map = new Map(); // currency -> { income, expense }
    for (const e of accountingEntriesInRange) {
      const cur = (e.currency || 'N/A').toUpperCase();
      const amt = safeNumber(e.amount);
      if (!map.has(cur)) map.set(cur, { income: 0, expense: 0 });
      const bucket = map.get(cur);
      if (e.type === 'INCOME') bucket.income += amt;
      else if (e.type === 'EXPENSE') bucket.expense += amt;
    }
    return Array.from(map.entries()).map(([currency, v]) => ({
      currency,
      income: v.income,
      expense: v.expense,
      net: v.income - v.expense,
    }));
  }, [accountingEntriesInRange]);

  const ledgerNetSummary = useMemo(() => {
    // multi-currency: show per currency net
    const rows = ledgerTotalsByCurrency
      .slice()
      .sort((a, b) => b.net - a.net)
      .map(r => ({
        label: r.currency,
        value: r.net,
        income: r.income,
        expense: r.expense,
      }));
    return rows;
  }, [ledgerTotalsByCurrency]);

  /* -------- KPI computed values -------- */
  const activeCars = useMemo(() => cars.filter(c => c.active).length, [cars]);
  const inactiveCars = useMemo(() => cars.length - activeCars, [cars, activeCars]);

  const rentalTotalsAll = useMemo(() => {
    // sum rentals across filtered reservations by currency
    const map = new Map();
    for (const r of filteredReservations) {
      if (r.rentalAmount == null || !r.rentalCurrency) continue;
      const cur = String(r.rentalCurrency).toUpperCase();
      map.set(cur, (map.get(cur) || 0) + safeNumber(r.rentalAmount));
    }
    return map;
  }, [filteredReservations]);

  const depositTotalsAll = useMemo(() => {
    const map = new Map();
    for (const r of filteredReservations) {
      if (r.depositAmount == null || !r.depositCurrency) continue;
      const cur = String(r.depositCurrency).toUpperCase();
      map.set(cur, (map.get(cur) || 0) + safeNumber(r.depositAmount));
    }
    return map;
  }, [filteredReservations]);

  /* -------- admin vehicle lookup -------- */
  const vehicleOptions = useMemo(() => {
    const q = normalizeText(vehicleSearch);
    const sorted = cars.slice().sort((a, b) => carDisplayName(a).localeCompare(carDisplayName(b)));
    if (!q) return sorted;

    return sorted.filter(car => {
      const haystack = [
        car.name,
        car.plate,
        car.year,
        car.category,
        car.bodyType,
        car.transmission,
        car.fuelType,
        car.color,
        car.id,
      ].map(v => String(v || '')).join(' ');
      return normalizeText(haystack).includes(q);
    });
  }, [cars, vehicleSearch]);

  const selectedVehicle = useMemo(
    () => cars.find(car => car.id === selectedVehicleId) || null,
    [cars, selectedVehicleId]
  );

  const selectedVehicleReservationsAll = useMemo(() => {
    if (!selectedVehicleId) return [];
    return reservations
      .filter(reservation => reservationUsesVehicleInRange(reservation, selectedVehicleId))
      .sort(sortReservationsDesc);
  }, [reservations, selectedVehicleId]);

  const selectedVehicleReservationsInRange = useMemo(() => {
    if (!selectedVehicleId) return [];
    return selectedVehicleReservationsAll
      .filter(reservation => reservationUsesVehicleInRange(reservation, selectedVehicleId, monthRange))
      .sort(sortReservationsDesc);
  }, [selectedVehicleReservationsAll, selectedVehicleId, monthRange]);

  const selectedVehicleRentalTotals = useMemo(
    () => currencyTotalsFromReservations(selectedVehicleReservationsInRange, 'rentalAmount', 'rentalCurrency'),
    [selectedVehicleReservationsInRange]
  );

  const selectedVehicleDepositTotals = useMemo(
    () => currencyTotalsFromReservations(selectedVehicleReservationsInRange, 'depositAmount', 'depositCurrency'),
    [selectedVehicleReservationsInRange]
  );

  const selectedVehicleReservedDays = useMemo(
    () => reservedDaysForVehicle(selectedVehicleReservationsAll, selectedVehicleId, monthRange),
    [selectedVehicleReservationsAll, selectedVehicleId, monthRange]
  );

  const selectedVehicleLastReservation = useMemo(() => {
    const now = new Date();
    return selectedVehicleReservationsAll.find(r => {
      const dt = parseDate(r.start);
      return dt && dt < now;
    }) || null;
  }, [selectedVehicleReservationsAll]);

  const selectedVehicleNextReservation = useMemo(() => {
    const now = new Date();
    return selectedVehicleReservationsAll
      .slice()
      .sort((a, b) => {
        const ad = parseDate(a.start) || new Date(8640000000000000);
        const bd = parseDate(b.start) || new Date(8640000000000000);
        return ad.getTime() - bd.getTime();
      })
      .find(r => {
        const dt = parseDate(r.start);
        return dt && dt >= now;
      }) || null;
  }, [selectedVehicleReservationsAll]);

  function selectVehicle(vehicleId) {
    setSelectedVehicleId(vehicleId || '');
    setVehicleSearch('');
    if (vehicleId && vehicleLookupRef.current && typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        vehicleLookupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function openVehicleReservation(reservation) {
    if (!isAdmin) {
      showAlert('Only admins can open vehicle reservations from analytics.', 'error');
      return;
    }
    setDrawerInitial({ ...reservation, __kind: 'res' });
    setDrawerOpen(true);
  }

  async function onDrawerSaved() {
    await loadData();
  }

  /* -------- render guards -------- */
  if (!ready) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
      </div>
    );
  }

  if (!canViewAnalytics) {
    return (
      <div style={{ padding: 24 }}>
        <b>Not authorized.</b> Analytics is restricted to admins.
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
      </div>
    );
  }

  return (
    <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)' }}>
      <div
        style={{
          width: '100%',
          maxWidth: 1080,
          margin: '0 auto',
          padding: isMobile ? '12px 10px 24px' : '16px 12px 32px',
          display: 'grid',
          gap: 16,
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 22 }}>Analytics</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Operational overview + financial snapshot. No mixed-currency lies.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                padding: '4px 8px',
                borderRadius: 999,
                border: '1px solid #d1d5db',
                background: '#e5f7da',
                color: '#166534',
                whiteSpace: 'nowrap',
              }}
            >
              Analytics access
            </span>

            <Button
              type="button"
              style={{ background: 'white', whiteSpace: 'nowrap' }}
              onClick={refresh}
              disabled={!selectedCompanyId || loading}
              title="Reload cars/reservations/ledger"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
        </header>

        {/* Scope */}
        <section style={cardStyle()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, fontSize: 16 }}>Scope</h4>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                Agency + time window. Everything below uses the same range.
              </p>
            </div>

            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'right' }}>
              {loading ? (
                'Loading…'
              ) : filteredCount ? (
                `${filteredCount} reservations in range`
              ) : (
                'No reservations in range'
              )}
              {totalReservationsCount > 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  ({totalReservationsCount} total loaded)
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: 12,
              alignItems: 'flex-end',
            }}
          >
            <Field label="Agency">
              <Select
                value={selectedCompanyId || ''}
                onChange={e => setSelectedCompanyId(e.target.value || null)}
                disabled={!isAdmin}
              >
                {isAdmin ? (
                  <>
                    <option value="">Select agency…</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </>
                ) : (
                  companies
                    .filter(c => c.id === selectedCompanyId)
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))
                )}
              </Select>
            </Field>

            <Field label="Month filter (optional)">
              <Input
                type="month"
                value={monthFilter}
                onChange={e => setMonthFilter(e.target.value)}
              />

            </Field>
            <Button
              type="button"
              style={{ background: 'white' }}
              onClick={() => setMonthFilter('')}
            >
              All time
            </Button>
            <div style={{ fontSize: 12, color: '#9ca3af', minHeight: 32 }}>
              {selectedCompany
                ? `Showing: ${selectedCompany.name} · ${fmtMonthRangeLabel(monthRange, monthFilter)}`
                : 'No agency selected.'}
            </div>
          </div>
        </section>

        {!selectedCompany && (
          <section style={cardStyle()}>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
              Select an agency above to view analytics.
            </p>
          </section>
        )}

        {selectedCompany && (
          <>
            {/* KPI row */}
            <section style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <StatCard
                label="Reservations"
                value={fmtInt(reservationStats.total)}
                sub={monthRange ? 'In selected month' : 'All time (loaded)'}
                right={<Sparkline points={sparkPoints} />}
              />
              <StatCard
                label="Rental totals"
                value={formatCurrencyMap0(rentalTotalsAll)}
                sub="Sum of rentalAmount by currency"
              />
              <StatCard
                label="Deposits collected"
                value={formatCurrencyMap0(depositTotalsAll)}
                sub="Not revenue"
              />
              <StatCard
                label="Fleet"
                value={fmtInt(cars.length)}
                sub={`${fmtInt(activeCars)} active · ${fmtInt(inactiveCars)} inactive`}
              />
              {fleetUtilization && (
                <StatCard
                  label="Fleet utilization"
                  value={`${fleetUtilization.utilizationPct.toFixed(1)}%`}
                  sub={`Avg reserved days/car: ${fleetUtilization.avgReservedDaysPerCar.toFixed(1)}`}
                />
              )}
              {!fleetUtilization && (
                <StatCard
                  label="Fleet utilization"
                  value="—"
                  sub="Select a month to compute utilization"
                />
              )}
            </section>

            {/* Insights row */}
            <section style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              <MiniBarChart
                title="Status mix"
                subtitle={`Peak/day: ${trendPeak} · Total points: ${trendTotal}`}
                rows={topStatusRows}
              />
              <MiniBarChart
                title="Approval mix"
                rows={topApprovalRows}
              />
              <MiniBarChart
                title="Top channels (by reservations)"
                subtitle="Counts only (currency-safe)."
                rows={platformChartRows}
              />
              <MiniBarChart
                title="Top utilization (month only)"
                subtitle={monthRange ? 'Utilization % by car' : 'Select a month'}
                rows={topUtilization}
              />
            </section>

            {/* Admin vehicle lookup */}
            {isAdmin && (
              <section ref={vehicleLookupRef} style={cardStyle()}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 2, fontSize: 16 }}>
                      Vehicle lookup
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                      Admin-only vehicle details and related reservations for the selected agency.
                    </p>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0,
                      padding: '4px 8px',
                      borderRadius: 999,
                      border: '1px solid #d1d5db',
                      background: '#f9fafb',
                      color: '#374151',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Admin only
                  </span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'minmax(180px, 0.8fr) minmax(260px, 1.2fr) auto',
                    gap: 12,
                    alignItems: 'flex-end',
                  }}
                >
                  <Field label="Search vehicles">
                    <Input
                      value={vehicleSearch}
                      onChange={e => setVehicleSearch(e.target.value)}
                      placeholder="Name, plate, category..."
                    />
                  </Field>

                  <Field label="Vehicle">
                    <Select
                      value={selectedVehicleId}
                      onChange={e => selectVehicle(e.target.value)}
                      disabled={!cars.length}
                    >
                      <option value="">Select a vehicle...</option>
                      {vehicleOptions.map(car => (
                        <option key={car.id} value={car.id}>
                          {carDisplayName(car)}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Button
                    type="button"
                    style={{ background: 'white', whiteSpace: 'nowrap' }}
                    onClick={() => selectVehicle('')}
                    disabled={!selectedVehicleId && !vehicleSearch}
                  >
                    Clear
                  </Button>
                </div>

                {vehicleSearch && vehicleOptions.length === 0 ? (
                  <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                    No vehicles match that search.
                  </div>
                ) : null}

                {!selectedVehicle ? (
                  <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                    Select a vehicle to see its details and related reservations.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        marginTop: 14,
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
                        gap: 10,
                      }}
                    >
                      <VehicleFact label="Vehicle" value={selectedVehicle.name || '--'} />
                      <VehicleFact label="Plate" value={selectedVehicle.plate || '--'} />
                      <VehicleFact label="Status" value={selectedVehicle.active ? 'Active' : 'Inactive'} />
                      <VehicleFact label="Year" value={selectedVehicle.year || '--'} />
                      <VehicleFact label="Category" value={selectedVehicle.category || '--'} />
                      <VehicleFact label="Body" value={selectedVehicle.bodyType || '--'} />
                      <VehicleFact
                        label="Spec"
                        value={[selectedVehicle.fuelType, selectedVehicle.transmission].filter(Boolean).join(' / ') || '--'}
                      />
                      <VehicleFact
                        label="Seats / Color"
                        value={[
                          selectedVehicle.seats ? `${selectedVehicle.seats} seats` : '',
                          selectedVehicle.color,
                        ].filter(Boolean).join(' / ') || '--'}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
                        gap: 10,
                      }}
                    >
                      <VehicleFact
                        label="Reservations in range"
                        value={fmtInt(selectedVehicleReservationsInRange.length)}
                        sub={`${fmtInt(selectedVehicleReservationsAll.length)} total loaded`}
                      />
                      <VehicleFact
                        label="Reserved days"
                        value={fmtMoney0(selectedVehicleReservedDays)}
                        sub={monthRange ? 'Selected month' : 'All loaded reservations'}
                      />
                      <VehicleFact
                        label="Rental totals"
                        value={formatCurrencyMap0(selectedVehicleRentalTotals)}
                      />
                      <VehicleFact
                        label="Deposit totals"
                        value={formatCurrencyMap0(selectedVehicleDepositTotals)}
                      />
                      <VehicleFact
                        label="Last reservation"
                        value={selectedVehicleLastReservation ? formatDateShort(selectedVehicleLastReservation.start) : '--'}
                      />
                      <VehicleFact
                        label="Next reservation"
                        value={selectedVehicleNextReservation ? formatDateShort(selectedVehicleNextReservation.start) : '--'}
                      />
                      <VehicleFact label="Vehicle ID" value={selectedVehicle.id} />
                      <VehicleFact
                        label="Starting price"
                        value={selectedVehicle.startingPrice != null ? fmtMoney0(selectedVehicle.startingPrice) : '--'}
                      />
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: 10,
                          flexWrap: 'wrap',
                          marginBottom: 8,
                        }}
                      >
                        <div>
                          <h5 style={{ margin: 0, fontSize: 14, color: '#111827' }}>
                            Related reservations
                          </h5>
                          <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                            Uses the current month filter unless All time is selected.
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {fmtInt(selectedVehicleReservationsInRange.length)} shown
                        </div>
                      </div>

                      {selectedVehicleReservationsInRange.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#6b7280' }}>
                          No related reservations in this range.
                        </div>
                      ) : isMobile ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          {selectedVehicleReservationsInRange.map(reservation => (
                            <div
                              key={reservation.id}
                              style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 10,
                                padding: 12,
                                background: '#f9fafb',
                                display: 'grid',
                                gap: 8,
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>
                                    {reservation.reservationRef || reservation.id}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    {reservation.customerName || 'No customer listed'}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  onClick={() => openVehicleReservation(reservation)}
                                  style={{
                                    background: 'white',
                                    padding: '6px 10px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <ExternalLink size={14} />
                                  Open
                                </Button>
                              </div>
                              <div style={{ display: 'grid', gap: 4, fontSize: 12, color: '#4b5563' }}>
                                <div><strong style={{ color: '#111827' }}>Start:</strong> {formatDateTimeShort(reservation.start)}</div>
                                <div><strong style={{ color: '#111827' }}>End:</strong> {formatDateTimeShort(reservation.end)}</div>
                                <div><strong style={{ color: '#111827' }}>Vehicle window:</strong> {formatVehicleReservationWindow(reservation, selectedVehicleId, monthRange)}</div>
                                <div><strong style={{ color: '#111827' }}>Status:</strong> {getLifecycleStatus(reservation)}</div>
                                <div><strong style={{ color: '#111827' }}>Rental:</strong> {reservation.rentalCurrency ? `${reservation.rentalCurrency} ${fmtMoney0(reservation.rentalAmount)}` : '--'}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 12,
                              tableLayout: 'fixed',
                              wordWrap: 'break-word',
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={th}>Reservation</th>
                                <th style={th}>Customer</th>
                                <th style={th}>Start</th>
                                <th style={th}>End</th>
                                <th style={th}>Vehicle window</th>
                                <th style={th}>Status</th>
                                <th style={th}>Rental</th>
                                <th style={th}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedVehicleReservationsInRange.map(reservation => (
                                <tr key={reservation.id} style={{ borderTop: '1px solid #eee' }}>
                                  <td style={td}>{reservation.reservationRef || reservation.id}</td>
                                  <td style={td}>{reservation.customerName || '--'}</td>
                                  <td style={td}>{formatDateTimeShort(reservation.start)}</td>
                                  <td style={td}>{formatDateTimeShort(reservation.end)}</td>
                                  <td style={td}>{formatVehicleReservationWindow(reservation, selectedVehicleId, monthRange)}</td>
                                  <td style={td}>{getLifecycleStatus(reservation)}</td>
                                  <td style={td}>
                                    {reservation.rentalCurrency ? `${reservation.rentalCurrency} ${fmtMoney0(reservation.rentalAmount)}` : '--'}
                                  </td>
                                  <td style={td}>
                                    <Button
                                      type="button"
                                      onClick={() => openVehicleReservation(reservation)}
                                      style={{
                                        background: 'white',
                                        padding: '6px 10px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      <ExternalLink size={14} />
                                      Open
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </section>
            )}

            {/* Platforms table */}
            <section style={cardStyle()}>
              <h4 style={{ marginTop: 0, marginBottom: 4, fontSize: 16 }}>Channels / Platforms</h4>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6b7280' }}>
                Bookings by source. Rental/deposit totals shown by currency (no fake mixed totals).
              </p>

              {platformStats.length === 0 ? (
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  No platform data for this range.
                </div>
              ) : isMobile ? (
                <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
                  {platformStats.map((row) => (
                    <div
                      key={row.platform}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        padding: 12,
                        background: '#f9fafb',
                        display: 'grid',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{row.platform}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
                          {fmtInt(row.reservations)} bookings
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 6, fontSize: 12, color: '#4b5563' }}>
                        <div>
                          <strong style={{ color: '#111827' }}>Rental totals:</strong> {formatCurrencyMap0(row.rentalByCurrency)}
                        </div>
                        <div>
                          <strong style={{ color: '#111827' }}>Deposit totals:</strong> {formatCurrencyMap0(row.depositByCurrency)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                      tableLayout: 'fixed',
                      wordWrap: 'break-word',
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={th}>Platform</th>
                        <th style={{ ...th, textAlign: 'right' }}>Reservations</th>
                        <th style={th}>Rental totals</th>
                        <th style={th}>Deposit totals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platformStats.map(row => (
                        <tr key={row.platform} style={{ borderTop: '1px solid #eee' }}>
                          <td style={td}>{row.platform}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{fmtInt(row.reservations)}</td>
                          <td style={td}>{formatCurrencyMap0(row.rentalByCurrency)}</td>
                          <td style={td}>{formatCurrencyMap0(row.depositByCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Ledger */}
            <section style={cardStyle()}>
              <h4 style={{ marginTop: 0, marginBottom: 4, fontSize: 16 }}>
                Financial snapshot (ledger)
              </h4>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6b7280' }}>
                Income/expense/net from Accounting entries in the same range.
              </p>

              {ledgerTotalsByCurrency.length === 0 ? (
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  No ledger entries in this range.
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 10 }}>
                    <MiniBarChart
                      title="Net by currency"
                      subtitle="Positive is good. If net is negative consistently, your ops are trash."
                      rows={ledgerNetSummary.map(r => ({ label: r.label, value: r.value }))}
                      height={150}
                    />
                  </div>

                  {isMobile ? (
                    <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
                      {ledgerTotalsByCurrency.map((row) => (
                        <div
                          key={row.currency}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f9fafb',
                            display: 'grid',
                            gap: 6,
                          }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{row.currency}</div>
                          <div style={{ display: 'grid', gap: 4, fontSize: 12, color: '#4b5563' }}>
                            <div><strong style={{ color: '#111827' }}>Income:</strong> {fmtMoney0(row.income)}</div>
                            <div><strong style={{ color: '#111827' }}>Expense:</strong> {fmtMoney0(row.expense)}</div>
                            <div style={{ color: row.net >= 0 ? '#166534' : '#b91c1c', fontWeight: 800 }}>
                              Net: {fmtMoney0(row.net)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                          tableLayout: 'fixed',
                          wordWrap: 'break-word',
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={th}>Currency</th>
                            <th style={{ ...th, textAlign: 'right' }}>Income</th>
                            <th style={{ ...th, textAlign: 'right' }}>Expense</th>
                            <th style={{ ...th, textAlign: 'right' }}>Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledgerTotalsByCurrency.map(row => (
                            <tr key={row.currency} style={{ borderTop: '1px solid #eee' }}>
                              <td style={td}>{row.currency}</td>
                              <td style={{ ...td, textAlign: 'right' }}>{fmtMoney0(row.income)}</td>
                              <td style={{ ...td, textAlign: 'right' }}>{fmtMoney0(row.expense)}</td>
                              <td
                                style={{
                                  ...td,
                                  textAlign: 'right',
                                  color: row.net >= 0 ? '#166534' : '#b91c1c',
                                  fontWeight: 900,
                                }}
                              >
                                {fmtMoney0(row.net)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {monthRange && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
                      Ledger filtered to the same month as reservations.
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Per-car */}
            <section style={cardStyle()}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginBottom: 6,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 2, fontSize: 16 }}>
                    Per-car performance & utilization
                  </h4>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                    Reservations, rental totals by currency, and utilization when a month is selected.
                  </p>
                </div>

                {!monthRange ? (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    Select a month to compute utilization percentages.
                  </div>
                ) : null}
              </div>

              {carStats.length === 0 ? (
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  No cars or reservations yet.
                </div>
              ) : isMobile ? (
                <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
                  {carStats.map((c) => (
                    <div
                      key={c.carId}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        padding: 12,
                        background: '#f9fafb',
                        display: 'grid',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{c.plate || 'No plate listed'}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: c.active ? '#166534' : '#6b7280' }}>
                          {c.active ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 4, fontSize: 12, color: '#4b5563' }}>
                        <div><strong style={{ color: '#111827' }}>Reservations:</strong> {fmtInt(c.reservationCount)}</div>
                        <div><strong style={{ color: '#111827' }}>Total rental:</strong> {formatCurrencyMap0(c.totalRental)}</div>
                        <div><strong style={{ color: '#111827' }}>Days reserved:</strong> {fmtMoney0(c.reservedDays)}</div>
                        <div><strong style={{ color: '#111827' }}>Utilization:</strong> {c.utilizationPct != null ? `${c.utilizationPct.toFixed(1)}%` : 'â€”'}</div>
                        <div><strong style={{ color: '#111827' }}>Last reservation:</strong> {c.lastReservation ? c.lastReservation.toLocaleDateString() : 'â€”'}</div>
                      </div>
                      {isAdmin && (
                        <Button
                          type="button"
                          onClick={() => selectVehicle(c.carId)}
                          style={{
                            background: 'white',
                            padding: '7px 10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                          }}
                        >
                          <ExternalLink size={14} />
                          Details
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 560,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 12,
                      tableLayout: 'fixed',
                      wordWrap: 'break-word',
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={th}>Car</th>
                        <th style={th}>Plate</th>
                        <th style={{ ...th, textAlign: 'center' }}>Active</th>
                        <th style={{ ...th, textAlign: 'right' }}>Reservations</th>
                        <th style={th}>Total rental</th>
                        <th style={{ ...th, textAlign: 'right' }}>Days reserved</th>
                        <th style={{ ...th, textAlign: 'right' }}>Utilization %</th>
                        <th style={th}>Last reservation</th>
                        {isAdmin && <th style={th}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {carStats.map(c => (
                        <tr key={c.carId} style={{ borderTop: '1px solid #eee' }}>
                          <td style={td}>{c.name}</td>
                          <td style={td}>{c.plate || '—'}</td>
                          <td style={{ ...td, textAlign: 'center' }}>{c.active ? '✓' : '—'}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{fmtInt(c.reservationCount)}</td>
                          <td style={td}>{formatCurrencyMap0(c.totalRental)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            {fmtMoney0(c.reservedDays)}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>
                            {c.utilizationPct != null ? `${c.utilizationPct.toFixed(1)}%` : '—'}
                          </td>
                          <td style={td}>
                            {c.lastReservation ? c.lastReservation.toLocaleDateString() : '—'}
                          </td>
                          {isAdmin && (
                            <td style={td}>
                              <Button
                                type="button"
                                onClick={() => selectVehicle(c.carId)}
                                style={{
                                  background: 'white',
                                  padding: '6px 10px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <ExternalLink size={14} />
                                Details
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {toast && <ToastBox toast={toast} onClose={hideAlert} />}
      <ReservationDrawer
        open={drawerOpen && !!drawerInitial}
        mode="edit"
        initial={drawerInitial}
        companies={companies}
        cars={cars}
        claims={claims}
        useStringKeyCond={false}
        activeCompanyId={selectedCompanyId}
        readOnly={!isAdmin}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerInitial(null);
        }}
        onSaved={onDrawerSaved}
        pushAlert={pushAlert}
      />
      <AlertOverlay alert={alert} onClose={() => setAlert(null)} />
    </div>
  );
}

function VehicleFact({ label, value, sub }) {
  return (
    <div
      style={{
        minWidth: 0,
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#f9fafb',
        padding: 10,
      }}
    >
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0 }}>
        {label}
      </div>
      <div
        title={String(value || '')}
        style={{
          marginTop: 4,
          fontSize: 13,
          fontWeight: 800,
          color: '#111827',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>{sub}</div> : null}
    </div>
  );
}

/* ---------------- Toast component ---------------- */

function ToastBox({ toast, onClose }) {
  if (!toast) return null;
  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 360,
      }}
    >
      <div
        style={{
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          boxShadow: '0 10px 25px rgba(15,23,42,0.18)',
          border: '1px solid rgba(148,163,184,0.5)',
          background:
            toast.variant === 'success'
              ? '#e7f9ec'
              : toast.variant === 'error'
                ? '#fef2f2'
                : '#f9fafb',
          color:
            toast.variant === 'success'
              ? '#166534'
              : toast.variant === 'error'
                ? '#991b1b'
                : '#111827',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 16 }}>
          {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '!' : 'ℹ'}
        </div>
        <div style={{ flex: 1 }}>{toast.message}</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            margin: 0,
            fontSize: 14,
            lineHeight: 1,
            color: 'inherit',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ---------------- Shared styles ---------------- */

function cardStyle() {
  return {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    boxSizing: 'border-box',
  };
}

function kpiCard() {
  return {
    ...cardStyle(),
    padding: 14,
  };
}

function miniCard() {
  return {
    ...cardStyle(),
    padding: 12,
  };
}

const th = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid #e5e7eb',
  fontWeight: 700,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b7280',
  background: '#f9fafb',
};

const td = {
  padding: '6px 8px',
  verticalAlign: 'middle',
  fontSize: 13,
  color: '#111827',
};
